/* eslint react-hooks/exhaustive-deps: 0 */
import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { ddmmmyyyy, fmtDate } from "@/lib/dt";   // STEP 3 · B-7c: the one formatter
import SaveCancelBar from "@/components/SaveCancelBar";   // Batch 28 · Follow-up 1 · F2
import { useFormDraft } from "@/lib/useDrafts";

const CONCLUSIONS = ["", "unmodified", "qualified", "adverse", "disclaimer"];
const BASES = ["", "misstatement_material", "misstatement_pervasive",
                    "inability_material", "inability_pervasive"];
const FRAMEWORK_CHECKS = [
  "framework_acceptable", "framework_applied_consistently",
  "accounting_policies_selected_and_applied", "disclosures_adequate",
  "terminology_appropriate", "presentation_and_structure_appropriate",
];

export default function W178Conclusion() {
  const { current, currentEngId } = useEngagement();
  const { t, lang } = useI18n();
  const [saved, setSaved] = useState(null);
  // Batch 28 · Follow-up 1 · F2: the conclusion is a draft — nothing is written while typing / ticking; Save sends the changed
  // fields once (the existing POST); Cancel puts back the saved values; the sign buttons wait until saved.
  const f = useFormDraft(saved);
  const [saving, setSaving] = useState(false);
  const s = saved ? f.value : null;
  const [dSel, setDSel] = useState({ preparer: "", reviewer: "", partner: "" });
  const reload = async () => {
    if (!currentEngId) return;
    const r = await api.get(`/engagements/${currentEngId}/w17/conclusion`, { _silent: true });
    setSaved(r.data);
  };
  useEffect(() => { f.reset(); reload(); }, [currentEngId]);
  const patch = (p) => f.setMany(p);
  const saveDraft = async () => {
    if (!f.dirty) return;
    setSaving(true);
    try {
      const r = await api.post(`/engagements/${currentEngId}/w17/conclusion`,
                                    { patch: f.patch }, { _silent: true });
      setSaved(r.data); f.reset(); toast.success(t("w17_8.saved"));
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
    finally { setSaving(false); }
  };
  const signoff = async (role) => {
    // K3 — DO NOT wrap with U+2068 / U+2069 in the SAVED payload.
    // Isolate chars are for display only (screen), never for storage.
    // Backend also strips any stragglers on read (belt-and-suspenders).
    const d = dSel[role] ? ddmmmyyyy(format(dSel[role], "yyyy-MM-dd")) : ddmmmyyyy(new Date());   // today = the firm's day
    try {
      const r = await api.post(`/engagements/${currentEngId}/w17/conclusion/signoff`,
                                    { role, date: d }, { _silent: true });
      setSaved(r.data); toast.success(t("w17_8.signedOk"));
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
  };
  if (!current) return <div className="text-sm text-muted-foreground p-6">{t("common.noEng")}</div>;
  const isReview = (current?.engagement_type || "audit") === "review";
  if (!isReview) return <div className="p-6 text-sm text-muted-foreground" data-testid="w17-8-audit-only">{t("w17_8.notForAudit")}</div>;
  if (!s) return <div className="p-6 text-sm text-muted-foreground" data-testid="w17-8-loading">{t("common.loading")}</div>;
  return (
    <div className="space-y-6" data-testid="w17-8-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="w17-8-title">
          <span dir="ltr" className="font-mono text-[13px] text-muted-foreground me-2">508(r)</span>
          {t("w17_8.title")}
        </h1>
        <SignOffBar itemType="w17_8" itemKey="W17.8" saveFirst={f.dirty} />   {/* Batch 11 · A2 */}
        <p className="text-sm text-muted-foreground">{t("w17_8.subtitle")}</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base normal-case">{t("w17_8.sufficiencyTitle")}</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-[12px]">
          <label className="flex items-center gap-2">
            <Checkbox checked={s.evidence_sufficient === true}
                        onCheckedChange={v => patch({ evidence_sufficient: !!v })}
                        data-testid="w17-8-evidence-sufficient" />
            {t("w17_8.evidenceSufficient")}
          </label>
          {s.evidence_sufficient !== true && (
            <div className="space-y-2 pt-2">
              <Textarea placeholder={t("w17_8.limitationDiscussedPh")}
                              value={s.limitation_discussed || ""}
                              onChange={e => f.setMany({limitation_discussed: e.target.value})}
                              className="h-16 text-[11px]" data-testid="w17-8-limitation-disc" />
              <Textarea placeholder={t("w17_8.limitationEffectPh")}
                              value={s.limitation_effect || ""}
                              onChange={e => f.setMany({limitation_effect: e.target.value})}
                              className="h-16 text-[11px]" data-testid="w17-8-limitation-effect" />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base normal-case">{t("w17_8.frameworkTitle")}</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-[12px]">
          {FRAMEWORK_CHECKS.map(k => (
            <label key={k} className="flex items-center gap-2">
              <Checkbox checked={!!(s.framework_checks || {})[k]}
                          onCheckedChange={v => patch({ framework_checks: {...(s.framework_checks || {}), [k]: !!v} })}
                          data-testid={`w17-8-fw-${k}`} />
              {t(`w17_8.fw.${k}`)}
            </label>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base normal-case">{t("w17_8.conclusionTitle")}</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-[12px]">
          <div>
            <div className="mb-1 font-medium">{t("w17_8.conclusionType")}</div>
            <select value={s.conclusion_type || ""}
                     onChange={e => patch({ conclusion_type: e.target.value })}
                     className="h-8 rounded border px-1 text-[11px]"
                     data-testid="w17-8-conclusion-type">
              {CONCLUSIONS.map(c => <option key={c} value={c}>{c ? t(`w17_8.conc.${c}`) : t("w17_8.pickConclusion")}</option>)}
            </select>
          </div>
          {["qualified","adverse","disclaimer"].includes(s.conclusion_type) && (
            <div className="space-y-2 rounded border bg-amber-50 p-2 dark:bg-amber-950/30">
              <div className="text-[11px] font-semibold text-amber-800">{t("w17_8.basisRequired")}</div>
              <select value={s.basis || ""}
                       onChange={e => patch({ basis: e.target.value })}
                       className="h-8 rounded border px-1 text-[11px]"
                       data-testid="w17-8-basis">
                {BASES.map(b => <option key={b} value={b}>{b ? t(`w17_8.basisOpt.${b}`) : t("w17_8.pickBasis")}</option>)}
              </select>
              <Textarea placeholder={t("w17_8.basisNotePh")}
                              value={s.basis_note || ""}
                              onChange={e => f.setMany({basis_note: e.target.value})}
                              className="h-16 text-[11px]" data-testid="w17-8-basis-note" />
            </div>
          )}
          <label className="flex items-center gap-2">
            <Checkbox checked={!!s.eom_flag} onCheckedChange={v => patch({ eom_flag: !!v })}
                        data-testid="w17-8-eom-flag" />
            {t("w17_8.eom")}
          </label>
          {s.eom_flag && (
            <Textarea placeholder={t("w17_8.eomNotePh")} value={s.eom_note || ""}
                            onChange={e => f.setMany({eom_note: e.target.value})}
                            className="h-16 text-[11px]" data-testid="w17-8-eom-note" />
          )}
          <label className="flex items-center gap-2">
            <Checkbox checked={!!s.om_flag} onCheckedChange={v => patch({ om_flag: !!v })}
                        data-testid="w17-8-om-flag" />
            {t("w17_8.om")}
          </label>
          {s.om_flag && (
            <Textarea placeholder={t("w17_8.omNotePh")} value={s.om_note || ""}
                            onChange={e => f.setMany({om_note: e.target.value})}
                            className="h-16 text-[11px]" data-testid="w17-8-om-note" />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base normal-case">{t("w17_8.signoffsTitle")}</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-[12px]">
          {["preparer","reviewer","partner"].map(role => (
            <div key={role} className="flex items-center gap-2 rounded border p-2">
              <div className="w-24 font-medium">{t(`w17_8.role.${role}`)}</div>
              <div className="flex-1">
                {s[`${role}_signoff`] ? (
                  <span data-testid={`w17-8-signoff-${role}-set`}>
                    {/* K3 — wrap the date with U+2068 / U+2069 AT DISPLAY
                        so RTL never reverses the DD-MMM-YYYY. Any stray
                        isolate chars still on the stored value (legacy
                        H4 payloads) are stripped by the backend on read. */}
                    {t("w17_8.signedBy")
                      .replace("{name}", s[`${role}_signoff`].name || "—")
                      .replace("{date}",
                        s[`${role}_signoff`].date
                          ? `\u2068${String(s[`${role}_signoff`].date).replace(/[\u2068\u2069]/g, "")}\u2069`
                          : "—")}
                  </span>
                ) : (
                  <span className="text-muted-foreground">{t("w17_8.notSigned")}</span>
                )}
              </div>
              {!s[`${role}_signoff`] && (<>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-[11px]"
                           data-testid={`w17-8-signoff-${role}-date`}>
                    <CalendarIcon className="me-1 h-3 w-3" />
                    {dSel[role] ? fmtDate(format(dSel[role], "yyyy-MM-dd")) : t("w17_8.pickDate")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={dSel[role] || undefined}
                              onSelect={(d) => setDSel({...dSel, [role]: d})} />
                </PopoverContent>
              </Popover>
              <Button size="sm" onClick={() => signoff(role)} disabled={f.dirty || (role === "partner" && !s.ready)}
                       data-testid={`w17-8-signoff-${role}-btn`}>
                {t("w17_8.sign")}
              </Button>
              </>)}
            </div>
          ))}
          {!s.ready && (
            <div className="text-[11px] text-amber-800" data-testid="w17-8-not-ready">
              {t("w17_8.partnerBlockedNotReady")}
            </div>
          )}
        </CardContent>
      </Card>
      <SaveCancelBar dirty={f.dirty} saving={saving} onSave={saveDraft} onCancel={f.reset} lang={lang} testid="w17-8-save-bar" />
    </div>
  );
}
